import _extends from "@babel/runtime/helpers/extends";
import { createVNode as _createVNode } from "vue";
import { defineComponent, h, Fragment, Text } from 'vue';
import { unified } from 'unified';
import rehypePrism from 'rehype-prism-plus';
import rehypeAttrs from 'rehype-attr';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import rehypeRewrite from 'rehype-rewrite';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { VFile } from 'vfile';
import { html } from 'property-information';
import { octiconLink } from './nodes/octiconLink';
import { copyElement } from './nodes/copyElement';
import { childrenToVue } from './utils/ast-to-vue';
import { uriTransformer } from './utils/uri-transformer';
import aliasLanguage from './plugins/aliasLanguage';
var markdownPreview = {
  rehypePlugins: {
    type: Object,
    default: []
  },
  remarkPlugins: {
    type: Object,
    default: []
  },
  components: {
    type: Object,
    default: {}
  },
  source: {
    type: String,
    default: ''
  },
  sourcePos: {
    type: Boolean,
    default: false
  },
  rawSourcePos: {
    type: Boolean,
    default: false
  },
  includeElementIndex: {
    type: Boolean,
    default: false
  },
  skipHtml: {
    type: Boolean,
    default: false
  },
  linkTarget: {
    type: [Function, String]
  },
  transformLinkUri: {
    type: Function,
    default: uriTransformer
  },
  transformImageUri: {
    type: Function,
    default: uriTransformer
  }
};
var getCodeStr = function getCodeStr(data, code) {
  if (data === void 0) {
    data = [];
  }
  if (code === void 0) {
    code = '';
  }
  data.forEach(node => {
    if (node.type === 'text') {
      code += node.value;
    } else if (node.type === 'element' && node.children && Array.isArray(node.children)) {
      code += getCodeStr(node.children);
    }
  });
  return code;
};
export default defineComponent({
  name: 'MarkdownPreview',
  props: markdownPreview,
  setup(props, _ref) {
    var {
      slots
    } = _ref;
    function processor() {
      return unified().use(remarkParse).use(remarkGfm).use(props.remarkPlugins || []).use(remarkRehype, {
        allowDangerousHtml: true
      }).use(rehypeAttrs, {
        properties: 'attr'
      }).use(rehypeRaw).use(aliasLanguage).use(rehypeSlug).use(rehypeAutolinkHeadings).use(rehypePrism, {
        ignoreMissing: true
      }).use(rehypeRewrite, {
        rewrite: node => {
          if (node.type == 'element' && /h(1|2|3|4|5|6)/.test(node.tagName) && node.children && Array.isArray(node.children) && node.children.length > 0) {
            var child = node.children[0];
            if (child && child.type === 'element' && child.properties) {
              child.properties = _extends({
                className: 'anchor'
              }, child.properties);
              child.children = [octiconLink()];
            }
          }
          if (node.type === 'element' && node.tagName === 'pre') {
            var code = getCodeStr(node.children);
            node.children.push(copyElement(code));
          }
        }
      }).use(props.rehypePlugins || []);
    }
    return () => {
      var children = slots && slots.default ? slots.default().filter(item => item.type === Text).map(item => item.children) : [];
      var file = new VFile();
      file.value = children && children.length > 0 ? children.join() : props.source;
      var prc = processor();
      var hastNode = prc.runSync(prc.parse(file), file);
      if (hastNode.type !== 'root') {
        throw new TypeError('Expected a `root` node');
      }
      var result = h(Fragment, {}, childrenToVue(hastNode, {
        listDepth: 0,
        schema: html,
        components: props.components,
        sourcePos: props.sourcePos,
        rawSourcePos: props.rawSourcePos,
        includeElementIndex: props.includeElementIndex,
        skipHtml: props.skipHtml,
        linkTarget: props.linkTarget,
        transformLinkUri: props.transformLinkUri,
        transformImageUri: props.transformImageUri
      }));
      return _createVNode("div", {
        "class": "markdown-body"
      }, [result]);
    };
  }
});